import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'ask-ai:expanded'

const readPersistedExpanded = (): boolean => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

const writePersistedExpanded = (value: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(value))
  } catch {
    // Storage unavailable (private mode / quota / disabled).
    // Fall back to in-memory state only.
  }
}

export interface IUseAskAiPanelReturn {
  isOpen: boolean
  isExpanded: boolean
  open: () => void
  close: () => void
  toggle: () => void
  toggleExpand: () => void
}

export const useAskAiPanel = (): IUseAskAiPanelReturn => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [isExpanded, setIsExpanded] = useState<boolean>(readPersistedExpanded)

  const open = useCallback((): void => setIsOpen(true), [])

  const close = useCallback((): void => setIsOpen(false), [])

  const toggle = useCallback((): void => {
    setIsOpen(prev => !prev)
  }, [])

  const toggleExpand = useCallback((): void => {
    setIsExpanded(prev => (isOpen ? !prev : prev))
  }, [isOpen])

  useEffect((): void => {
    writePersistedExpanded(isExpanded)
  }, [isExpanded])

  useEffect((): (() => void) | undefined => {
    if (!isOpen) return undefined

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return (): void => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  return { isOpen, isExpanded, open, close, toggle, toggleExpand }
}
